import clsx from 'clsx'
import { dropdownSurfaceClassName, dropdownSectionHeaderClassName } from './dropdown-styles'

type RecentSearchesProps = {
  /** Most recent first. */
  recents: string[]
  activeIndex: number
  onActiveIndexChange: (index: number) => void
  /** Runs the picked query. */
  onSelect: (query: string) => void
  onClearHistory: () => void
  /** Referenced by the input's `aria-controls`. */
  listboxId: string
  /** Builds the row id the input points `aria-activedescendant` at. */
  getOptionId: (index: number) => string
}

/**
 * The queries this user last ran in this matter, offered when the field is focused and empty.
 *
 * Stage 0 of search: it shows before any typing, and is replaced by the suggestions dropdown as
 * soon as there is a query to suggest against.
 */
export function RecentSearches({ recents, activeIndex, onActiveIndexChange, onSelect, onClearHistory, listboxId, getOptionId }: RecentSearchesProps) {
  return (
    <div className={dropdownSurfaceClassName}>
      <div className="flex h-8 items-center justify-between gap-4 px-2">
        <span className={dropdownSectionHeaderClassName} id={`${listboxId}-label`}>
          Recent Searches
        </span>

        <button
          type="button"
          className="shrink-0 text-sm leading-5 text-brand-500 hover:text-brand-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500"
          // Mousedown would blur the input and close the dropdown before the click lands.
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClearHistory}
        >
          Clear History
        </button>
      </div>

      <div role="listbox" id={listboxId} aria-labelledby={`${listboxId}-label`}>
        {recents.map((query, index) => (
          <div
            key={query}
            id={getOptionId(index)}
            role="option"
            aria-selected={index === activeIndex}
            className={clsx(
              'flex h-10 w-full cursor-pointer items-center rounded p-2 text-left text-sm leading-5 tracking-[-0.15px]',
              index === activeIndex ? 'bg-brand-neutral-100 text-brand-neutral-800' : 'text-brand-neutral-700'
            )}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onSelect(query)}
            onMouseEnter={() => onActiveIndexChange(index)}
          >
            <span className="truncate">{query}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
